"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

const serviceOptions = [
  { value: "ppf", label: "Kaitsekiletamine PPF", base: 650 },
  { value: "keraamika", label: "Keraamiline kaitse", base: 450 },
  { value: "toonimine", label: "Klaaside toonimine", base: 80 },
] as const;

const packageOptions = [
  { value: "partial", label: "Esiosa / osaliselt", multiplier: 1 },
  { value: "full", label: "Täiskate", multiplier: 1.7 },
  { value: "details", label: "Üksikud detailid", multiplier: 0.65 },
] as const;

export default function PriceCalculator() {
  const searchParams = useSearchParams();
  const [service, setService] = useState("ppf");
  const [packageType, setPackageType] = useState("partial");

  useEffect(() => {
    const requestedService = searchParams.get("service");
    if (requestedService && serviceOptions.some((option) => option.value === requestedService)) {
      setService(requestedService);
    }
  }, [searchParams]);

  const estimate = useMemo(() => {
    const selectedService = serviceOptions.find((item) => item.value === service) ?? serviceOptions[0];
    const selectedPackage = packageOptions.find((item) => item.value === packageType) ?? packageOptions[0];
    const amount = Math.round(selectedService.base * selectedPackage.multiplier);

    return {
      label: selectedService.label,
      estimate: amount,
      packageLabel: selectedPackage.label,
    };
  }, [packageType, service]);

  return (
    <section className="rounded-sm border border-white/10 bg-primary/80 p-8 md:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.2)]">
      <div className="mb-8 max-w-2xl">
        <p className="mb-3 text-[12px] uppercase tracking-[0.2em] text-accent">Hinnakalkulaator</p>
        <h2 className="font-heading text-3xl font-bold text-text-main">Ligikaudne hind kiireks orientatsiooniks</h2>
        <p className="mt-3 text-sm leading-relaxed text-text-muted">
          Valige teenus ja töömaht ning saame teile kiiresti ettekujutuse ligikaudsest hinnast. Täpse pakkumise saamiseks võtke meiega ühendust.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-6">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-text-main">Teenus</span>
            <select
              value={service}
              onChange={(event) => setService(event.target.value)}
              className="w-full rounded-sm border border-white/10 bg-secondary px-4 py-3 text-sm text-text-main outline-none ring-0"
            >
              {serviceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-text-main">Maht</span>
            <select
              value={packageType}
              onChange={(event) => setPackageType(event.target.value)}
              className="w-full rounded-sm border border-white/10 bg-secondary px-4 py-3 text-sm text-text-main outline-none ring-0"
            >
              {packageOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="rounded-sm border border-accent/20 bg-secondary/80 p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-accent">Eelnev hinnang</p>
          <p className="mt-4 font-heading text-4xl font-bold text-text-main">alates {estimate.estimate}€</p>
          <p className="mt-2 text-sm text-text-muted">
            {estimate.label} · {estimate.packageLabel}
          </p>
          <Link
            href={`/kontakt?service=${service}`}
            aria-label={`Küsi täpset pakkumist ${estimate.label} jaoks`}
            className="mt-6 inline-flex rounded-sm bg-accent px-5 py-3 font-heading font-bold text-primary transition-colors duration-200 hover:bg-[#d8b884]"
          >
            Küsi täpset pakkumist
          </Link>
        </div>
      </div>
    </section>
  );
}
